import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { CurrencyFormatService } from './currency-format.service';
import { BinderPromo } from '@/models/promo.model';
import { Binder } from '@/models/binder.model';
import { BinderAssembly } from '@/models/binder-assembly.model';
import { CalcTotals, ParsedDraw } from '@/models/master-json.model';
import { round } from 'lodash';
import { allocateDraws, numberToCents } from '@/utility/money-utils';


@Injectable({
  providedIn: 'root'
})
export class ContractMathService {
  higharcPlanSubscription!: Subscription;
  exemptionSubscription!: Subscription;
  exemptionStatus: boolean | null = null;

  constructor(private currencyFormatService: CurrencyFormatService) { 
  }
 private toCents = (dollars: number | null | undefined) => numberToCents(dollars ?? 0);
 private fromCents = (cents: number) => cents / 100;

 formatPrice(price: number): string {
   return this.currencyFormatService.format(price) || '';
 }

 calculatePromos(binder: Binder): { promoTotal: number, binderPromos: BinderPromo[] } {
   const promoTotalCents =
     (binder.binderPromos ?? []).reduce((tot, promo) => {
       // promo.promotions.amount is assumed to be dollars (number)
       return tot + this.toCents(promo.promotions.amount);
     }, 0);

   return {
     promoTotal: this.fromCents(promoTotalCents),
     binderPromos: binder.binderPromos ?? []
   };
 }

 calculateOptionsTotal(assemblies: BinderAssembly[]): {
   optionSubtotal: number, engineeringTotal: number, solarTotal: number, optionTotal: number
 } {
   let optionSubtotalC = 0;
   let engineeringTotalC = 0;
   let solarTotalC = 0;

   if (assemblies?.length) {
     for (const a of assemblies) {
       const qty = a.Quantity ?? 1;
       const priceC = this.toCents(a.Price ?? 0);
       const lineC = priceC * qty;

       if (a.Type !== 'S') optionSubtotalC += lineC;
       if (a.Type === 'F') engineeringTotalC += lineC;
       if (a.CategoryID === 882) solarTotalC += lineC;
     }
   }

   const optionTotalC = optionSubtotalC - engineeringTotalC;

   return {
     optionSubtotal: this.fromCents(optionSubtotalC),
     engineeringTotal: this.fromCents(engineeringTotalC),
     solarTotal: this.fromCents(solarTotalC),
     optionTotal: this.fromCents(optionTotalC),
   };
 }

 calculateTax(binder: Binder, rate: number, taxedTotal: number): { total: number, rate: number } {
   const taxRate = rate ?? 0;
   let convertedRate: number;

   // Arizona TPT (your original formula)
   if (binder.SiteState === 'AZ') {
     convertedRate = (((taxRate / 100) * 0.65) / (1 + (((taxRate / 100) * 0.65))));
   } else {
     convertedRate = taxRate / 100;
   }

   const taxedTotalC = this.toCents(taxedTotal);
   const totalTaxC = Math.round(taxedTotalC * convertedRate); // HALF_UP to cents
   return { total: this.fromCents(totalTaxC), rate: convertedRate };
 }

 calculateDesignValues(
   contractEx: boolean | null,
   total: number,
   engineering: number,
   feeOverride?: number | null
 ): {
   engineering: number;
   designTotal: number;
   initialDesign: number;
   firstFee: number;
   remainder: number;
 } {
   const designRate = contractEx ? 0 : (feeOverride ?? 0.06);

   const totalC = this.toCents(total);
   const engineeringC = this.toCents(engineering);

   const initialDesignC = Math.round(totalC * designRate);
   const designTotalC = initialDesignC + engineeringC;
   const firstFeeC = Math.round(designTotalC * 0.5);
   const remainderC = designTotalC - firstFeeC;

   return {
     engineering: this.fromCents(engineeringC),
     designTotal: this.fromCents(designTotalC),
     initialDesign: this.fromCents(initialDesignC),
     firstFee: this.fromCents(firstFeeC),
     remainder: this.fromCents(remainderC),
   };
 }

 calculateBalance(binder: Binder, _tax: number, total: number): number {
   const totalC = this.toCents(total);
   const processingC = this.toCents(binder.Processing_Fee ?? 0);
   const creditC = this.toCents(binder.Credit ?? 0);
   return this.fromCents(totalC - processingC - creditC);
 }


 generateTotals(binder: Binder, taxRate: number, assemblies: BinderAssembly[]): CalcTotals {
   const totals: CalcTotals = new CalcTotals();

   totals.options = this.calculateOptionsTotal(assemblies);
   totals.promos = this.calculatePromos(binder);

   const travelC = this.toCents(binder.Travel ?? 0);
   const planPriceC = this.toCents(binder.PlanPrice ?? 0);
   const optionSubtotalC = this.toCents(totals.options.optionSubtotal);

   const subtotalC = travelC + planPriceC + optionSubtotalC;
   totals.travelTotal = this.fromCents(travelC);
   totals.planPrice = this.fromCents(planPriceC);
   totals.subtotal = this.fromCents(subtotalC);
   totals.dueLessTax = totals.subtotal;

   // Fees
   const processingFeeC = this.toCents(binder.Processing_Fee ?? 0);
   totals.processingFee = this.fromCents(processingFeeC);

   // Design
   totals.design = this.calculateDesignValues(
     binder.contractEx,
     totals.subtotal,
     totals.options.engineeringTotal,
     binder.feeOverride
   );

   // Tax
   const { total: taxDollars, rate: taxRateDecimal } = this.calculateTax(binder, taxRate, totals.subtotal);
   const taxC = this.toCents(taxDollars);
   totals.tax = this.fromCents(taxC);

   // Savings (round each line to cents)
   const savingsLines: { item: string; totalC: number; taxSaving: boolean }[] = [];
   const designTotalC = this.toCents(totals.design.designTotal);
   const solarTotalC = this.toCents(totals.options.solarTotal);

   const pushDesignSaving = (label: string) => {
     const amtC = Math.round(designTotalC * taxRateDecimal);
     savingsLines.push({ item: label, totalC: amtC, taxSaving: true });
   };

   switch (binder.SiteState) {
     case 'WA':
       pushDesignSaving('Design Service Tax Savings');
       if (solarTotalC > 0) {
         const solarSaveC = Math.round(solarTotalC * taxRateDecimal);
         savingsLines.push({ item: 'Solar Tax Savings', totalC: solarSaveC, taxSaving: true });
       }
       break;
     case 'OR':
       pushDesignSaving('Design Service Surcharge Savings');
       break;
     case 'AZ':
       pushDesignSaving('Design Service Tax Savings');
       break;
   }

   totals.savings.reductions = savingsLines.map(l => ({
     item: l.item,
     total: this.fromCents(l.totalC),
     reduction: this.formatPrice(this.fromCents(l.totalC)),
     taxSaving: l.taxSaving,
   }));

   const taxSavingsTotalC = savingsLines.filter(x => x.taxSaving).reduce((s, x) => s + x.totalC, 0);
   const nonTaxSavingsTotalC = savingsLines.filter(x => !x.taxSaving).reduce((s, x) => s + x.totalC, 0);
   const savingsTotalC = taxSavingsTotalC + nonTaxSavingsTotalC;
   totals.savings.total = this.fromCents(savingsTotalC);

   const contractTotalC = subtotalC + taxC - savingsTotalC;
   totals.contractTotal = this.fromCents(contractTotalC);

   const balanceC = contractTotalC - processingFeeC;
   totals.balance = this.fromCents(balanceC);

   //changed from contractTotalC to balanceC, so that the dispersal balance considers the processing fee, as per teams conversation with John Ristic - 12/11/25

   const dispersalBalanceC = binder.contractEx
     ? balanceC
     : (balanceC - designTotalC);

   totals.dispersalBalance = this.fromCents(dispersalBalanceC);

   return totals;
 }

 parseDraws(binder: Binder, totals: CalcTotals): ParsedDraw[] {
   const out: ParsedDraw[] = [];
   if (!binder?.Schedules?.Draws?.length) return out;

   const sorted = [...binder.Schedules.Draws].sort((a, b) => a.DrawID - b.DrawID);

   const lines = sorted.map((d: any, idx: number) => ({
     event: d.Title,
     fixedPct: idx < sorted.length - 1 ? (d.Percentage ?? 0) : undefined,
     percentage: idx === sorted.length - 1 ? 'Remainder' : undefined,
   }));

   const allocated = allocateDraws(totals.dispersalBalance, lines);

   for (const line of allocated) {
     out.push({
       Event: line.event,
       Percentage: line.percentage,
       amount: this.fromCents(line.amountCents),
       Total: this.formatPrice(this.fromCents(line.amountCents)),
     });
   }
   return out;
 }
}